import { Typography, Flex, Empty } from 'antd';
import { motion, AnimatePresence } from 'motion/react';
import { WorkflowDTOType } from '../../../../schemas';
import { useGetQCPendingItemsInfiniteQuery } from '../../../../store/workflows/workflowApi';
import WorkflowItemHorizontal from './WorkflowItemHorizontal';
import { WorkflowItemHorizontalSkeleton } from './WorkflowItemHorizontalSkeleton';
import { useFetchOnListEnd } from '@admin-panel/hooks';

const QCPendingItems = ({ workflow }: { workflow: WorkflowDTOType }) => {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetQCPendingItemsInfiniteQuery(
      {
        workflowId: workflow.id,
        runId: workflow.currentWorkflowRunId,
      },
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
      },
    );

  const items = data?.pages.flat() || [];
  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  if (!workflow) return null;
  if (isLoading)
    return (
      <Flex vertical gap={16} style={{ padding: '16px' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <WorkflowItemHorizontalSkeleton key={i} />
        ))}
      </Flex>
    );
  if (isError)
    return <Typography.Text type="danger">Error loading QC Pending Items</Typography.Text>;

  return (
    <motion.div
      style={{ padding: '16px' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Flex vertical gap={16}>
        {items.length > 0 ? (
          <AnimatePresence mode="popLayout">
            {items.map((item, idx) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  layout: { duration: 0.2 },
                }}
                whileHover={{ scale: 1.02 }}
                style={{ transformOrigin: 'center' }}
              >
                <WorkflowItemHorizontal
                  data={item}
                  workflow={workflow}
                  purpose="PENDING"
                  lastItemRef={idx === items.length - 1 ? lastItemRef : undefined}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Empty description="No pending items found." />
          </motion.div>
        )}
      </Flex>
    </motion.div>
  );
};

export default QCPendingItems;
